import axios from "axios";
import { Storage } from "@/services/Storage";
import { errorHandler } from "@/services/errorHandler";

export default {
  name: 'customer-onboarding',
  state: {
    customers: [],
    customerSignups: [],
    currentSignup: null,
    loading: {
      customers: false,
      signups: false,
      details: false
    },
    stats: {
      totalCustomers: 0,
      pendingSignups: 0,
      completedSignups: 0
    }
  },
  getters: {
    getCustomers: state => state.customers,
    getCustomerSignups: state => state.customerSignups,
    getCurrentSignup: state => state.currentSignup,
    getLoadingState: state => state.loading,
    getStats: state => state.stats
  },
  mutations: {
    setCustomers(state, customers) {
      state.customers = customers;
    },
    setCustomerSignups(state, signups) {
      state.customerSignups = signups;
    },
    setCurrentSignup(state, signup) {
      state.currentSignup = signup;
    },
    setLoading(state, { type, value }) {
      state.loading[type] = value;
    },
    setStats(state, stats) {
      state.stats = { ...state.stats, ...stats };
    }
  },
  actions: {
    // Fetch all customers with onboarding status
    fetchCustomers({ commit }, payload) {
      commit('setLoading', { type: 'customers', value: true });

      // Add date range from global state if available
      const params = {
        ...payload,
        ...this.getters.getGlobalDateRange
      };

      return axios.get(`${ticketApi}smartInvoiceOnboard/lytSI/customerSignups/search`, { params })
        .then(({ data }) => {
          const customers = data.response || [];
          commit('setCustomers', customers);
          commit('setStats', {
            totalCustomers: customers.length,
            pendingSignups: customers.filter(c => c.status === 'Pending').length,
            completedSignups: customers.filter(c => c.status === 'Completed').length
          });
          return customers;
        })
        .catch(err => errorHandler.tomcatError(err))
        .finally(() => commit('setLoading', { type: 'customers', value: false }));
    },

    // Fetch signups for a specific customer
    fetchCustomerSignups({ commit }, customerId) {
      commit('setLoading', { type: 'signups', value: true });

      return axios.get(`${smartInvoiceSignupApi}customer/${customerId}/signups`)
        .then(({ data }) => {
          commit('setCustomerSignups', data.response || []);
          return data.response;
        })
        .catch(err => errorHandler.tomcatError(err))
        .finally(() => commit('setLoading', { type: 'signups', value: false }));
    },

    // Fetch details for a specific signup
    fetchSignupDetails({ commit }, signupId) {
      commit('setLoading', { type: 'details', value: true });

      return axios.get(`${smartInvoiceSignupApi}signup/${signupId}`)
        .then(({ data }) => {
          commit('setCurrentSignup', data.response);
          return data.response;
        })
        .catch(err => errorHandler.tomcatError(err))
        .finally(() => commit('setLoading', { type: 'details', value: false }));
    },

    // Update signup status
    updateSignupStatus({ dispatch }, { signupId, status }) {
      const user = Storage.getCurrentUser();
      const payload = {
        id: signupId,
        status: status,
        processedBy: user.Username
      };

      return axios.post(`${smartInvoiceSignupApi}signup-status/update`, payload)
        .then(() => {
          return dispatch('fetchSignupDetails', signupId);
        })
        .catch(err => errorHandler.tomcatError(err, true));
    },

    // Add a note to a signup
    addSignupNote({ dispatch }, { signupId, content }) {
      const user = Storage.getCurrentUser();
      const payload = {
        signupId: signupId,
        content: content,
        user: user.Username
      };

      return axios.post(`${smartInvoiceSignupApi}notes`, payload)
        .then(() => {
          return dispatch('fetchSignupDetails', signupId);
        })
        .catch(err => errorHandler.tomcatError(err, true));
    },

    // Fetch branches for a signup
    fetchSignupBranches({ commit }, signupId) {
      return axios.get(`${smartInvoiceSignupApi}signup-branches/${signupId}`)
        .then(({ data }) => {
          return data.response;
        })
        .catch(err => errorHandler.tomcatError(err));
    },

    // Fetch logs for a signup
    fetchSignupLogs({ commit }, signupId) {
      return axios.get(`${smartInvoiceSignupApi}signup-logs/${signupId}`)
        .then(({ data }) => {
          return data.response;
        })
        .catch(err => errorHandler.tomcatError(err));
    },

    // Fetch notes for a signup
    fetchSignupNotes({ commit }, signupId) {
      return axios.get(`${smartInvoiceSignupApi}notes-by-signup-id/${signupId}`)
        .then(({ data }) => {
          return data.response;
        })
        .catch(err => errorHandler.tomcatError(err));
    }
  }
};
